/**
 * Open Graph 설정
 * 
 * 이 파일에서 모든 OG 메타 태그를 중앙 관리합니다.
 * SNS 공유 시 표시되는 정보를 여기서 수정하세요.
 */

export interface OpenGraphConfig {
  // 기본 정보
  title: string;
  description: string;
  siteName: string;
  url: string;
  
  // 이미지
  image: string;
  imageAlt: string;
  imageWidth?: string;
  imageHeight?: string;
  
  // Twitter Card
  twitterCard: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterSite?: string;
  twitterCreator?: string;
  
  // 추가 정보
  type: 'website' | 'article' | 'product' | 'profile';
  locale: string;
  
  // SEO
  keywords?: string[];
  author?: string;
  themeColor?: string;
}

/**
 * 기본 Open Graph 설정
 * 
 * 수정 방법:
 * 1. title: 페이지 제목 (50-60자 권장)
 * 2. description: 페이지 설명 (150-160자 권장)
 * 3. image: 썸네일 이미지 URL (1200x630px 권장)
 * 4. url: 사이트 URL
 */
export const openGraphConfig: OpenGraphConfig = {
  // 기본 정보
  title: 'MONEYBALL',
  description: 'Find value in memes that nobody else can see.',
  siteName: 'MONEYBALL',
  url: 'https://moneyball.money',

  // 이미지 (public/opengraph.png)
  image: 'https://moneyball.money/og.jpg',
  imageAlt: 'MONEYBALL - Token Analysis Platform',
  imageWidth: '2118',
  imageHeight: '1112',
  
  // Twitter Card
  twitterCard: 'summary_large_image',
  twitterSite: '@Moneyball_ai',
  twitterCreator: '@Moneyball_ai',
  
  // 추가 정보
  type: 'website',
  locale: 'en_US',
  
  // SEO
  keywords: [
    'crypto',
    'token analysis',
    'smart money',
    'crypto insights',
    'blockchain',
    'DeFi',
    'token research',
    'crypto analytics',
  ],
  author: 'MONEYBALL Team',
  themeColor: '#D0DE72',
};

/**
 * OG 이미지 서버 베이스 URL (검색 모드 시 동적 og:image에 사용).
 * 예: https://og.example.com → 이미지 URL은 https://og.example.com/api/og/admin-mod?token=CA
 */
const OG_SERVER_BASE = 'https://moneyball-og.vercel.app';

/** URL 쿼리 section 값: admin-mod | smart-money(OG 서버) / main(기본 이미지) */
export type OgSection = 'admin-mod' | 'smart-money' | 'main';

/**
 * 검색 모드(토큰 CA)일 때 사용할 og:image URL.
 * section=main → 기본 이미지(public/og.jpg), section=smart-money|admin-mod → OG 서버 동적 이미지.
 * @param tokenAddress - 토큰 CA
 * @param section - URL의 section 쿼리 값 (smart-money | admin-mod | main)
 */
export function getOgImageUrlForToken(
  tokenAddress: string,
  section?: string | null
): string {
  if (section === 'main') return openGraphConfig.image;
  if (!OG_SERVER_BASE || !tokenAddress?.trim()) return openGraphConfig.image;
  const path: 'admin-mod' | 'smart-money' =
    section === 'smart-money' || section === 'admin-mod' ? section : 'admin-mod';
  return `${OG_SERVER_BASE}/api/og/${path}?token=${encodeURIComponent(tokenAddress.trim())}`;
}

export function hasOgServerConfigured(): boolean {
  return OG_SERVER_BASE.length > 0;
}

/**
 * 페이지별 커스텀 OG 설정을 위한 헬퍼 함수
 * 
 * 사용 예시:
 * const customOG = createCustomOpenGraph({
 *   title: 'Token Analysis - BTC',
 *   description: 'Detailed analysis for Bitcoin token...',
 * });
 */
export function createCustomOpenGraph(overrides: Partial<OpenGraphConfig>): OpenGraphConfig {
  return {
    ...openGraphConfig,
    ...overrides,
  };
}
